import { createContext, useContext, useMemo, ReactNode } from 'react';
import type { ConversationMessagePersona } from '@duke-hq/ui/components/Conversation/ConversationMessage';
import { useGetPersonaById } from './tiptap/simulation/dBlock/useGetPersonaById';

export interface PersonaResolver {
  getPersonaById: (personaId: string) => ConversationMessagePersona;
  personasReady: boolean;
}

const DUKE_FALLBACK: ConversationMessagePersona = { name: 'Duke', avatarUrl: null };

// -- PersonaResolverContext ----------------------------------------------------
// Lets the chat editor resolve a personaId to its name/avatar without each block
// reaching for trpc itself. The default provider wires the existing trpc-backed
// hook, so the running app is unchanged; tests and Storybook can wrap a subtree
// in PersonaResolverContext.Provider with a static resolver instead. This is the
// seam that lets the editable node and the static card share one persona source
// and lets the editable node render in Storybook without a trpc client.
const PersonaResolverContext = createContext<PersonaResolver>({
  getPersonaById: () => DUKE_FALLBACK,
  personasReady: false,
});

export function PersonaResolverProvider({ children }: { children: ReactNode }) {
  // `getPersonaById` is intentionally a stable reference reading personas through
  // a ref, so loading them doesn't re-render anything. The flow editor's bubbles
  // read it live and no longer re-render incidentally, so they'd be stuck on the
  // Duke fallback. Give the resolver a fresh identity once personas load (keyed on
  // `personasReady`) so each bubble re-renders that once, then stays stable.
  const { getPersonaById, personasReady } = useGetPersonaById();
  const resolver = useMemo(
    () => ({
      getPersonaById: (personaId: string) => getPersonaById(personaId),
      personasReady,
    }),
    [getPersonaById, personasReady],
  );
  return (
    <PersonaResolverContext.Provider value={resolver}>{children}</PersonaResolverContext.Provider>
  );
}

export function usePersonaResolver(): PersonaResolver {
  return useContext(PersonaResolverContext);
}
